import { createFileRoute, useParams } from '@tanstack/react-router';
import { Card, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';

/**
 * Public CMS page render. The path `/page/:slug` is the same shape the .NET
 * system uses (PagePreview.razor) so M20 (App-Review URL stability) holds.
 *
 * We use plain fetch instead of tRPC because this surface must be reachable
 * for unauthenticated visitors and for Meta's App-Review reviewers.
 */
function CmsPagePublic() {
  const { slug } = useParams({ from: '/page/$slug' });
  const q = useQuery({
    queryKey: ['cms-page', slug],
    queryFn: async () => {
      // Hits /api/page/:slug — vite's dev proxy forwards /api/* to the
      // Fastify server. The public /page/:slug URL is also served by the
      // API for direct linking (Meta App Review etc.); the alias lets dev
      // and prod share the same fetch path.
      const res = await fetch(`/api/page/${slug}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Not found');
      const json = (await res.json()) as {
        ok: true;
        page: { title: string; content: string | null; slug: string };
      };
      return json.page;
    },
  });

  return (
    <Container size="md" py="xl">
      {q.isPending ? (
        <Stack align="center">
          <Loader />
        </Stack>
      ) : q.isError ? (
        <Card>
          <Title order={3}>Page not found</Title>
          <Text c="dimmed">No page exists at /page/{slug}</Text>
        </Card>
      ) : (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Stack gap="md">
            <Title order={1}>{q.data.title}</Title>
            {q.data.content && (
              // CMS HTML authored by SuperAdmin. Defense-in-depth (CA-024):
              // sanitize with DOMPurify so a compromised/over-broad admin edit
              // can't inject active content, independent of the SPA CSP.
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.data.content) }} />
            )}
          </Stack>
        </motion.div>
      )}
    </Container>
  );
}

export const Route = createFileRoute('/page/$slug')({ component: CmsPagePublic });
